/**
 * Teamspace chooser — the STANDALONE top-bar shell, rendered in the notification bell's old slot
 * (spec `26-spec-topnav-0826.md` §3, T981). Gated by `TOP_NAV_TEAM_SWITCHER` at the call site.
 *
 * Same list as the profile-menu sub-menu row (`TeamSwitcher`), different shell: Radix sub-menu
 * parts can't live outside a parent `DropdownMenu`, so a top-bar control needs its own trigger +
 * `DropdownMenu`. Everything else — the [ALL] entry, its gating, the ROOT relabel, the one-team
 * guard — comes from `TeamScopeList`.
 *
 * `compact` is the responsive rung: label + chevron normally, icon-only when the nav is cramped.
 * It NEVER hides — losing the scope indicator would make the [ALL] scope invisible.
 */

import { useTranslation } from 'react-i18next';
import { ChevronDown, Users } from 'lucide-react';
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@scio3/ui';
import { useTeamNoun } from '@/hooks/useTeamNoun';
import { TeamScopeList, useCanSwitchTeamScope, useTeamScopeLabel } from './TeamScopeList';

interface TeamScopeButtonProps {
  /** Icon-only trigger (no label, no chevron) — the narrow-nav state. */
  compact?: boolean;
}

export function TeamScopeButton({ compact }: TeamScopeButtonProps) {
  const { t } = useTranslation();
  const { noun } = useTeamNoun();
  const canSwitchTeamScope = useCanSwitchTeamScope();
  const scopeLabel = useTeamScopeLabel();

  if (!canSwitchTeamScope) return null;

  const trigger = (
    <DropdownMenuTrigger asChild>
      <Button
        variant="ghost"
        size={compact ? 'icon-sm' : 'sm'}
        className={cn(
          'shrink-0 cursor-pointer text-muted-foreground hover:bg-sidebar-active/60 hover:text-foreground',
          !compact && 'gap-1.5',
        )}
        // Icon-only state has no visible text; the labeled state must NOT be overridden by an
        // aria-label (WAI-ARIA: prefer the visible label).
        aria-label={compact ? t('Switch {{noun}}', { noun }) : undefined}
        data-testid="team-switcher-trigger"
      >
        <Users className="h-4 w-4 shrink-0" />
        {!compact && (
          <>
            <span className="min-w-0 max-w-[140px] truncate text-sm font-medium">{scopeLabel}</span>
            <ChevronDown className="h-3 w-3 shrink-0" />
          </>
        )}
      </Button>
    </DropdownMenuTrigger>
  );

  return (
    <DropdownMenu>
      {compact ? (
        // Icon-only: the current scope has nowhere to show, so the tooltip carries it.
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>{trigger}</TooltipTrigger>
            <TooltipContent side="bottom">{scopeLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        trigger
      )}
      <DropdownMenuContent align="end" sideOffset={4} className="w-72">
        <TeamScopeList />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
